import { Modal, Table, Tag, Tooltip, Button, Dropdown, Space } from "antd";
import React, { useState, useEffect } from "react";
import {
  DownCircleFilled,
  InfoCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { request } from "../../helper/axios_helper";
import {
  getTestCaseDetail,
  getTestCaseSummary,
} from "../../service/suite.service";
import { arrayFilter } from "../../helper/array_helper";
import RunTaskModal from "./RunTaskModal";
import moment from "moment";

/**
 * SpecSuitesModal
 *
 * Single modal with two views — suites list and test cases.
 * Switching between them feels like in-place navigation,
 * not separate modals stacking on top of each other.
 *
 * Props:
 *   isOpen   — boolean
 *   onClose  — function
 *   specName — string
 */
function SpecSuitesModal({ isOpen, onClose, specName }) {
  // ─── View state — "suites" or "testcases" ────────────────
  const [view, setView] = useState("suites");

  // ─── Suites data ──────────────────────────────────────────
  const [suiteData, setSuiteData] = useState([]);
  const [suitesLoading, setSuitesLoading] = useState(false);

  // ─── Test case data ───────────────────────────────────────
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [testCaseSummary, setTestCaseSummary] = useState([]);
  const [nestedData, setNestedData] = useState([]);
  const [testCasesLoading, setTestCasesLoading] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);

  // ─── Load suites when modal opens ────────────────────────
  useEffect(() => {
    if (isOpen && specName) {
      setSuitesLoading(true);
      request(
        "GET",
        `/api/auth/suite/fetch/name/${encodeURIComponent(specName)}`,
      )
        .then((res) => setSuiteData(res.data))
        .catch(() => setSuiteData([]))
        .finally(() => setSuitesLoading(false));
    }
  }, [isOpen, specName]);

  // ─── Load test cases when suite is selected ───────────────
  useEffect(() => {
    if (selectedSuite) {
      setTestCasesLoading(true);
      getTestCaseSummary(selectedSuite.testSuiteId)
        .then((res) => {
          if (res.status === 200) {
            setTestCaseSummary(res.data);
            return getTestCaseDetail(selectedSuite.testSuiteId);
          }
        })
        .then((res) => {
          if (res) setNestedData(res.data);
        })
        .finally(() => setTestCasesLoading(false));
    }
  }, [selectedSuite]);

  // ─── Reset everything on close ────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setView("suites");
      setSuiteData([]);
      setSelectedSuite(null);
      setTestCaseSummary([]);
      setNestedData([]);
    }
  }, [isOpen]);

  // ─── Navigation ───────────────────────────────────────────
  const navigateToTestCases = (record) => {
    setSelectedSuite(record);
    setView("testcases");
  };

  const navigateBackToSuites = () => {
    setView("suites");
    setSelectedSuite(null);
    setTestCaseSummary([]);
    setNestedData([]);
  };

  // ─── Modal title — changes with view ─────────────────────
  const modalTitle =
    view === "suites" ? (
      <span>
        Test Suites — {specName}{" "}
        <Tooltip title="All test suites linked to this control document">
          <InfoCircleOutlined
            style={{ color: "rgba(0,0,0,.45)", fontSize: 14 }}
          />
        </Tooltip>
      </span>
    ) : (
      <Space size={8}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={navigateBackToSuites}
          style={{ padding: "0 4px", fontWeight: 600 }}
        >
          {specName}
        </Button>
        <span style={{ color: "#bbb" }}>/</span>
        <span>{selectedSuite?.testSuiteName}</span>
      </Space>
    );

  // ─── Suite table columns ───────────────────────────────────
  const suiteColumns = [
    {
      title: "ID",
      dataIndex: "testSuiteId",
      key: "testSuiteId",
      width: "15%",
      sorter: (a, b) => a.testSuiteId.localeCompare(b.testSuiteId),
    },
    {
      title: "Type",
      dataIndex: "testSuiteType",
      key: "testSuiteType",
      width: "12%",
      render: (text) => {
        const colorMap = { Specification: "blue", Database: "purple" };
        return <Tag color={colorMap[text] || "default"}>{text}</Tag>;
      },
      filters: [
        { text: "Specification", value: "Specification" },
        { text: "Database", value: "Database" },
        { text: "default", value: "default" },
      ],
      onFilter: (value, record) => record.testSuiteType === value,
    },
    {
      title: "Test Suite Name",
      dataIndex: "testSuiteName",
      key: "testSuiteName",
      sorter: (a, b) => a.testSuiteName.localeCompare(b.testSuiteName),
    },
    {
      title: "Tests",
      dataIndex: "testCaseCount",
      key: "testCaseCount",
      width: "8%",
      sorter: (a, b) => a.testCaseCount - b.testCaseCount,
      render: (text, record) => (
        <a
          onClick={() => navigateToTestCases(record)}
          style={{ cursor: "pointer" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "12%",
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: "15%",
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        moment(a.createdDate, "DD-MM-YYYY HH:mm:ss") -
        moment(b.createdDate, "DD-MM-YYYY HH:mm:ss"),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: "View Tests",
                onClick: () => navigateToTestCases(record),
              },
            ],
          }}
          overlayClassName="table-row-actions-dropdown"
        >
          <a className="table-more-options">
            Options <DownCircleFilled />
          </a>
        </Dropdown>
      ),
    },
  ];

  // ─── Test case expanded row ────────────────────────────────
  const expandedRowRender = (record) => {
    const nestedColumns = [
      {
        title: "Column",
        dataIndex: "columnName",
        key: "columnName",
        width: "40%",
      },
      { title: "Value", dataIndex: "columnValue", key: "columnValue" },
    ];
    return (
      <Table
        className="nested-table"
        columns={nestedColumns}
        dataSource={arrayFilter(nestedData, record.testName)}
        size="small"
        bordered
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
        }}
      />
    );
  };

  // ─── Test case table columns ───────────────────────────────
  const testCaseColumns = [
    {
      title: "Test Type",
      dataIndex: "testType",
      key: "testType",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.testType.localeCompare(b.testType),
    },
    {
      title: "Test Name",
      dataIndex: "testName",
      key: "testName",
    },
    {
      title: "Test Count",
      dataIndex: "totalCount",
      key: "totalCount",
    },
  ];

  // ─── Footer — changes with view ───────────────────────────
  const footer =
    view === "suites"
      ? [
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]
      : [
          <Button
            key="back"
            icon={<ArrowLeftOutlined />}
            onClick={navigateBackToSuites}
          >
            Back to Suites
          </Button>,
          <Button
            key="run"
            type="primary"
            onClick={() => setRunModalOpen(true)}
          >
            Run Suite
          </Button>,
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ];

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        title={modalTitle}
        width="80vw"
        footer={footer}
        centered
        destroyOnClose={false}
      >
        {/* ─── Suites view ─────────────────────────────── */}
        {view === "suites" && (
          <Table
            loading={suitesLoading}
            columns={suiteColumns}
            dataSource={suiteData}
            rowKey="testSuiteId"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30"],
            }}
            size="middle"
          />
        )}

        {/* ─── Test cases view ─────────────────────────── */}
        {view === "testcases" && (
          <Table
            loading={testCasesLoading}
            className="components-table-demo-nested"
            columns={testCaseColumns}
            expandable={{
              rowExpandable: (record) =>
                record.testType === "Column Level" ||
                record.testType === "Field Level",
              expandedRowRender,
            }}
            dataSource={testCaseSummary}
            rowKey="key"
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30"],
            }}
            size="middle"
          />
        )}
      </Modal>

      {/* Run Suite modal */}
      {selectedSuite && (
        <RunTaskModal
          isOpen={runModalOpen}
          openModal={() => setRunModalOpen(!runModalOpen)}
          suiteName={selectedSuite.testSuiteName}
          specName={specName}
          suiteId={selectedSuite.testSuiteId}
        />
      )}
    </>
  );
}

export default SpecSuitesModal;
